import _ from 'lodash';
import { Person } from './BaseModels';
import dateUtil from '../utils/dates';
import WhiteLabelController from '../core/WhiteLabelController';
import { capitalizeName } from '../components/filters/utils';

export class User extends Person {

	static STATUSES = {
		PENDING: 'pending',
		REJECTED: 'rejected',
		ACTIVE: 'active',
		EXPIRED: 'expired'
	};

	static fromJSON(json) {
		const fullname = _.trimStart(`${json.first_name || ''} ${json.last_name || ''}`);
		return _.extend(super.fromJSON(json), {
			id: json.id,
			perms: json.perms,
			authToken: json.token,
			company: json.company,
			phoneNumber: json.phone_number,
			phoneNumberVerified: json.phone_number_verified,
			profileImageUrl: json.profile_image_url,
			logoUrl: json.logo_url,
			hasAcceptedGDPR: json.has_accepted_GDPR,
			registrationDate: dateUtil.dateToLocalZone(json.date_joined),
			lastLoginDate: dateUtil.dateToLocalZone(json.last_login),
			annualAdSpend: json.annual_ad_spend,
			userType: json.user_type,
			isImpersonating: json.isImpersonating,
			// only for few users
			status: json.status,
			domain: json.domain,
			region: json.region_id,
			country: json.country_id,
			countryName: json.country_name,
			countryAlphaCode: json.country_alpha_code,
			domainId: json.domain_id || json.domain?.id,
			role: json.role,
			roleId: json.role_id,
			isActive: json.is_active,
			firstName: json.first_name,
			lastName: json.last_name,
			userFullName: !_.isEmpty(fullname) ? capitalizeName(fullname) : 'N/A',
			productTypeID: json.iq_product_selected,
			productTypeName: json.iq_product_name,
			userSiteLogo: json.logo_url,
			persona: json.persona,
			userGroup: json.user_group,
			uiTheme: json.ui_theme || 'light'
		});
	}

	static toJSON(data) {
		const payload = {
			first_name: data.firstName,
			last_name: data.lastName,
			company: data.company,
			phone_number: data.phoneNumber,
			email: data.email,
			has_accepted_GDPR: data.has_accepted_GDPR,
			verify_password: data.verifyPassword,
			password: data.password,
			annual_ad_spend: data.annualAdSpend,
			user_type: data.userType,
			region_id: data.region,
			country_id: data.country
		};
		if (data.uiTheme !== undefined) {
			payload.ui_theme = data.uiTheme;
		}
		return payload;
	}

	get isAdminUser() {
		return this.checkAccess('admin');
	}

	get canAccessCPRScript() {
		return this.checkAccess('admin.cpr_script') || this.isAdminUser;
	}

	get canAccessDomainManager() {
		return this.checkAccess('domain_manager') || this.isAdminUser;
	}

	get canAccessSeeNotifications() {
		return this.checkAccess('in_app_notifications') || this.isAdminUser;
	}

	get canAccessNotificationsManagerCreate() {
		return this.checkAccess('in_app_notifications.manage_in_app_notifications') || this.isAdminUser;
	}

	get canAccessSupportDashboard() {
		return this.checkAccess('support_dashboard') || this.isAdminUser;
	}

	get canAccessSupportDashboardManage() {
		return this.checkAccess('support_dashboard.manage') || this.isAdminUser;
	}

	get canAccessSupportDashboardAutoBidV1() {
		return this.checkAccess('support_dashboard.auto_bid_v1') || this.isAdminUser;
	}

	get canAccessSupportDashboardAutoBidV2() {
		return this.checkAccess('support_dashboard.auto_bid_v2') || this.isAdminUser;
	}

	get canAccessSupportDashboardSalesforce() {
		return this.checkAccess('support_dashboard.salesforce') || this.isAdminUser;
	}

	get canAccessInspect() {
		return this.checkAccess('inspect') || this.isAdminUser;
	}

	get canAccessInspectManageAll() {
		return this.checkAccess('inspect.manage_all') || this.isAdminUser;
	}

	get canAccessInspectOptimizations() {
		return this.checkAccess('inspect.access_to_optimize_exclusions') || this.isAdminUser;
	}

	get canAccessInspectSuitability() {
		return this.checkAccess('inspect.brand_suitability') || this.isAdminUser;
	}

	get canAccessInspectFlagging() {
		return this.checkAccess('inspect.flag_inspection') || this.isAdminUser;
	}

	get canAccessInspectAnalyticsDashboard() {
		return this.checkAccess('inspect.access_inspect_analytics_dashboard') || this.isAdminUser;
	}

	get canAccessInspectExportAll() {
		return this.checkAccess('inspect.export_all') || this.isAdminUser;
	}

	get canAccessInspectNLPAgeClassification() {
		return this.checkAccess('inspect.nlp_age_classification') || this.isAdminUser;
	}

	get canAccessInspectTemplateShareRoles() {
		return this.checkAccess('inspect.template_share_with_roles') || this.isAdminUser;
	}

	get canAccessInspectTemplateShareUsers() {
		return this.checkAccess('inspect.template_share_with_users') || this.isAdminUser;
	}

	get canAccessInspectShare() {
		return this.canAccessInspectTemplateShareRoles || this.canAccessInspectTemplateShareUsers;
	}

	get canAccessMetaInspectBrightData() {
		return this.checkAccess('inspect.meta_campaigns_bright_data') || this.isAdminUser;
	}

	get canAccessMetaInspectMetaCampaigns() {
		return this.checkAccess('inspect.meta_campaigns_content') || this.isAdminUser;
	}

	get canAccessMetaInspect() {
		return this.canAccessMetaInspectBrightData || this.canAccessMetaInspectMetaCampaigns
			|| this.checkAccess('inspect.meta_campaigns') || this.isAdminUser;
	}

	get canAccessForecasting() {
		return this.checkAccess('pricing_tool') || this.isAdminUser;
	}

	get canAccessUserManager() {
		if (this.isImpersonating) return false;
		return this.checkAccess('user_management') || this.isAdminUser;
	}

	get canAccessUserManagerCreateRoles() {
		return this.checkAccess('user_management.create_user_roles') || this.isAdminUser;
	}

	get canAccessUserManagerAssignRoles() {
		return this.checkAccess('user_management.assign_user_roles') || this.isAdminUser;
	}

	get canAccessUserManagerEditPermissions() {
		return this.checkAccess('user_management.edit_custom_permissions') || this.isAdminUser;
	}

	get canAccessUserManagerEditUsersRoles() {
		return this.checkAccess('user_management.edit_user_roles') || this.isAdminUser;
	}

	get canAccessUserManagerDeleteRoles() {
		return this.checkAccess('user_management.delete_user_roles') || this.isAdminUser;
	}

	get canAccessUserManagerEditUserSettings() {
		return this.checkAccess('user_management.edit_user_settings') || this.isAdminUser;
	}

	get canAccessUserManagerExport() {
		return this.checkAccess('user_management.export_user_list') || this.isAdminUser;
	}

	get canAccessUserManagerLogInAsUser() {
		return this.checkAccess('user_management.log_in_as_user') || this.isAdminUser;
	}

	get canAccessPlatformSwitcher() {
		return this.checkAccess('platform_switcher') || this.isAdminUser;
	}

	get canAccessPlatformSwitcherActivateIq() {
		return true;
	}

	get canAccessPlatformSwitcherViewIq() {
		return this.checkAccess('platform_switcher.view_iq') || this.isAdminUser;
	}

	get canAccessPlatformSwitcherReportingIq() {
		return this.checkAccess('platform_switcher.reporting_iq') || this.isAdminUser;
	}

	get canAccessPlatformSwitcherAero() {
		return this.checkAccess('platform_switcher.aero') || this.isAdminUser;
	}

	get canAccessDataTracker() {
		return this.checkAccess('data_tracker') || this.isAdminUser;
	}

	get canAccessDataExplorer() {
		return this.checkAccess('data_explorer') || this.isAdminUser;
	}

	get canAccessServiceDesk() {
		return this.checkAccess('service_desk') || this.isAdminUser;
	}

	get canAccessExploreDashboardExport() {
		return this.checkAccess('ai_dashboard_export') || this.isAdminUser;
	}

	get canAccessWidgetLocker() {
		return this.checkAccess('ai_widget_locker') || this.isAdminUser;
	}

	get canAccessWidgetLockerDefault() {
		return this.checkAccess('ai_widget_locker.edit_default_view') || this.isAdminUser;
	}

	get canAccessWidgetLockerShare() {
		return this.checkAccess('ai_widget_locker.share_widgets') || this.isAdminUser;
	}

	get canAccessStreamliner() {
		return this.checkAccess('streamliner') || this.isAdminUser;
	}

	get canAccessQAAutomation() {
		return this.checkAccess('qa_health_checker') || this.isAdminUser;
	}

	get canAccessQAAutomationDelete() {
		return this.checkAccess('qa_health_checker.delete') || this.isAdminUser;
	}

	get canAccessStreamlinerPushGAds() {
		return this.checkAccess('streamliner.push_gads') || this.isAdminUser;
	}

	get canAccessStreamlinerMeta() {
		return this.checkAccess('streamliner.meta') || this.isAdminUser;
	}

	get canAccessStreamlinerMetaPushAdmin() {
		return this.checkAccess('streamliner.meta_push_admin') || this.isAdminUser;
	}

	get canAccessAdBidder() {
		return this.checkAccess('auto_bid') || this.isAdminUser;
	}

	get canAccessAdBidderPromptEngineeringEditor() {
		return this.checkAccess('auto_bid.prompt_engineering_editor') || this.isAdminUser;
	}

	get canAccessAdBidderPromptEngineeringViewer() {
		return this.checkAccess('auto_bid.prompt_engineering_viewer') || this.isAdminUser;
	}

	get canAccessRecipes() {
		return this.checkAccess('recipes') || this.isAdminUser;
	}

	get canAccessRecipesShare() {
		return this.checkAccess('recipes.share_recipes') || this.isAdminUser;
	}

	get canAccessAdBidderAgenticIntelligence() {
		return this.checkAccess('auto_bid.agentic_intelligence') || this.isAdminUser;
	}

	get canAccessPersonas() {
		return this.checkAccess('personas') || this.isAdminUser;
	}

	get canAccessPacingIQ() {
		return this.checkAccess('pacing_iq') || this.isAdminUser;
	}

	get canAccessIQExtension() {
		return this.checkAccess('iq_extension.dv360_extension') || this.checkAccess('iq_extension.gads_extension') || this.checkAccess('iq_extension') || this.isAdminUser;
	}

	get canAccessGAdsExtension() {
		return this.checkAccess('iq_extension.gads_extension') || this.isAdminUser;
	}

	get canAccessDV360Extension() {
		return this.checkAccess('iq_extension.dv360_extension') || this.isAdminUser;
	}

	checkDomainAccess(accessDisabled) {
		const hasDomainAccess = !WhiteLabelController.siteAssets.restricted.includes(accessDisabled);
		return hasDomainAccess;
	}

	checkAccess(accessName) {
		if (!_.isEmpty(this?.perms)) {
			return !!this.perms[accessName];
		}
		return false;
	}

}

export class PermissionsLog {
	static fromJSON(json) {
		return _.extend(new this(), {
			date: json.date,
			positiveAction: _.includes(json.action, 'added'),
			actionUser: json.acting_user,
			value: json.field_value || null,
			targetUser: json.target_user,
			description: json.summary,
			type: json.field_type
		})
	}
}

export class RoleLog {
	static fromJSON(json) {
		return _.extend(new this(), {
			date: dateUtil.formatToOnlyDateFormat(json.date),
			positiveAction: _.includes(json.action, 'added'),
			actionUser: json.acting_user,
			permission: json.permission || null,
			user: json.user || null,
			roleID: json.role_id,
			roleName: json.role_name,
			description: json.summary
		})
	}
}

export class CPRScriptItem {
	static fromJSON(json) {
		return _.extend(new this(), {
			message: json.message
		})
	}
	static toJSON(data) {
		return _.extend(new this(), {
			method: data.method
		})
	}
}
